// =====================================================================
// teamCardLogic — framework-free rendering decisions for the "Expert
// Team" grid card in LandingPage.jsx, extracted for Node's built-in test
// runner (no JSX runner in this repo; mirrors the pageHeaderLogic.mjs /
// dataBrowserLogic.mjs precedent).
//
// Why this exists: migrations/0023_users_email_optional_display_only.sql
// + scripts/import_team_profiles.js's --profile-only-order path let a
// person appear on the public team grid with NO project role (display_role
// stays NULL rather than guessed — this is a Ministry site with real
// named individuals, and inventing a role is out of scope). The card used
// to always render a <p> for member.role; with role absent that left an
// empty paragraph — a gap in the card that breaks its vertical rhythm and
// looks broken rather than deliberate. The card must render identically
// whether a role is present or not, just with one fewer line — not two
// visually different card layouts sitting in the same grid.
// =====================================================================

// A role is only "present" if it is a non-empty string after trimming —
// same non-destructive-empty-value convention scripts/import_team_
// profiles.js's isEmpty() uses server-side, applied client-side here so
// a whitespace-only value (which should never reach the API, but this
// must not trust that) doesn't render a blank line either.
function isPresentString(value) {
  return typeof value === 'string' && value.trim().length > 0
}

// Resolve which optional pieces of a team-grid card should render for a
// given member (the shape GET /api/public/team returns — see
// services/auth/routes/public.js). Only `hasRole` is used by LandingPage
// today; `hasAffiliation` is included for parity/future use and mirrors
// the affiliation conditional LandingPage.jsx already had in place.
export function resolveTeamCardSlots(member = {}) {
  return {
    hasRole: isPresentString(member.role),
    hasAffiliation: isPresentString(member.institutionShortName) || isPresentString(member.affiliation),
  }
}

// =====================================================================
// Two-tier "Expert Team" section (see LandingPage.jsx §"Team Section").
//
// The client supplied full write-ups (a biography) for 4 of the 11 team
// members; the other 7 have only a photo, name, and institution because
// the client hasn't sent those yet. Rendered as one flat grid the 7 read
// as broken/half-filled rather than deliberate, and 11 in a 4-column grid
// leaves a ragged 3-card final row. The fix is hierarchy: members with a
// bio become "featured" (larger card, bio excerpt, opens the full
// profile); everyone else sits in a quieter "Project team" tier (photo,
// name, institution, role only if present).
//
// The split is derived from the data (member.bio present or not) — never
// from a hardcoded name list or landing_order — specifically so that once
// the client supplies the missing 7 write-ups, every member becomes
// "featured" and the section collapses to one tier with no code change.
// That collapse (and its mirror — zero members with a bio) is the
// highest-value case to get right: see the "all bios" / "no bios" /
// single-member / empty-list tests in __tests__/teamCardLogic.test.mjs.
// =====================================================================

function hasBio(member) {
  return isPresentString(member?.bio)
}

// Trained PhD students are people the project trained, not project team
// members — they render in their own "Trained PhD Students" tier and are
// excluded from featured/rest. Keyed on the API's memberCategory (see
// migration 0037 / GET /api/public/team), never a hardcoded name list.
export const TRAINED_PHD_CATEGORY = 'trained_phd_student'

function isTrainedPhdStudent(member) {
  return member?.memberCategory === TRAINED_PHD_CATEGORY
}

// Partitions `members` into { featured, rest } — featured = has a bio,
// rest = everyone else. Order within each tier is preserved from the
// input (no re-sorting), and neither array is ever undefined: an empty
// or non-array input yields { featured: [], rest: [] } rather than
// throwing, since this runs against API data that may not have loaded
// yet.
export function splitTeamByTier(members) {
  const list = Array.isArray(members) ? members : []
  const featured = list.filter(hasBio)
  const rest = list.filter((m) => !hasBio(m))
  return { featured, rest }
}

// Full layout decision for the team section, built on top of
// splitTeamByTier. `showRestHeading` is the piece that prevents an
// orphan "Project team" heading: it is only true when BOTH tiers are
// non-empty. When everyone has a bio (rest is empty) or nobody does
// (featured is empty), the section renders as a single uniform grid —
// no empty tier, no heading floating over nothing.
export function resolveTeamSectionLayout(members) {
  const list = Array.isArray(members) ? members : []
  const trainedPhdStudents = list.filter(isTrainedPhdStudent)
  const team = list.filter((m) => !isTrainedPhdStudent(m))
  const { featured, rest } = splitTeamByTier(team)
  return {
    featured,
    rest,
    trainedPhdStudents,
    showFeaturedTier: featured.length > 0,
    showRestTier: rest.length > 0,
    showRestHeading: featured.length > 0 && rest.length > 0,
    showTrainedPhdTier: trainedPhdStudents.length > 0,
  }
}

// Default excerpt length for a featured card's bio preview. Short enough
// to keep featured cards a uniform height regardless of how long the
// full biography is; the card itself signals there's more via a "View
// full profile" affordance that opens TeamMemberModal.
export const BIO_EXCERPT_MAX_LENGTH = 140

// Truncates a bio to a card-sized excerpt without severing a word
// mid-character. Never returns a value longer than maxLength + 1 (the
// trailing ellipsis). A blank/whitespace-only/missing bio (null,
// undefined, '', '   ') resolves to '' — same "absent" convention as
// isPresentString/resolveTeamCardSlots — so a caller can treat '' as
// "render nothing" without a separate presence check.
export function truncateBioExcerpt(bio, maxLength = BIO_EXCERPT_MAX_LENGTH) {
  if (!isPresentString(bio)) return ''
  const trimmed = bio.trim()
  if (trimmed.length <= maxLength) return trimmed

  const cut = trimmed.slice(0, maxLength)
  const lastSpace = cut.lastIndexOf(' ')
  // Only break on a word boundary if one exists reasonably close to the
  // end of the cut — otherwise (e.g. one very long word) just hard-cut,
  // same fallback isPresentString-style helpers in this file use.
  const safeCut = lastSpace > 0 ? cut.slice(0, lastSpace) : cut
  return `${safeCut}…`
}
